import { uniq } from "lodash";
import { useMemo, type FC } from "react";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useDeepRootSelector, useRootSelector } from "@ds/hooks";

import { getPlayerStatusIndicatorWithNameAndLink } from "@ds/modules/devices/players/components/common/status-indicator";
import { selectPlayersByIds } from "@ds/modules/devices/players/redux/selectors";
import { selectTemplateExperienceRoles } from "@ds/modules/settings/projects/redux/selectors/experience-formation-selectors";
import { hasFormationRoles } from "@ds/modules/settings/projects/utils/formation-roles";
import { selectCurrentUserProjectId } from "@ds/modules/settings/users/redux/selectors";

import { MultipleValuesLabelWithLink, UnassignedLabel } from "@ds/components/forms/labels/common";
import { InfoSection } from "@ds/components/info-view";
import { isMultipleFieldValuesInAggregatedEntity } from "@ds/utils/entities";
import { composeDetailsRouteByEntityType } from "@ds/utils/router";

import { selectExperiencesByIds } from "../../redux/selectors";
import { getPlayerRole, getRoleTitle } from "../../utils/player-roles";

export const ExperiencePlayersInfoSection: FC<{ experience: Experience }> = ({ experience }) => {
  const projectId = useRootSelector(selectCurrentUserProjectId);
  const roles = useRootSelector(state => selectTemplateExperienceRoles(state, projectId));
  const aggregatedExperienceIds = useGetAggregatedEntityIds(experience);
  const aggregatedExperiencePlayers = useDeepRootSelector(state => {
    const aggregatedExperiences = selectExperiencesByIds(state, aggregatedExperienceIds);
    const aggregatedPlayersIds = uniq(aggregatedExperiences.flatMap(({ device_ids }) => device_ids).filter(_ => _));
    return selectPlayersByIds(state, aggregatedPlayersIds, "experience_order");
  });

  const infoBody = useMemo(() => {
    const hasMultipleValues = isMultipleFieldValuesInAggregatedEntity(experience, "device_ids");
    if (!aggregatedExperiencePlayers.length) {
      return <UnassignedLabel text="Experience(s) has no assigned players" />;
    } else if (hasMultipleValues) {
      return <MultipleValuesLabelWithLink to={composeDetailsRouteByEntityType(aggregatedExperiencePlayers)} />;
    }

    return aggregatedExperiencePlayers.map(entry => {
      const roleKey = hasFormationRoles(roles) ? getPlayerRole(experience.formation?.devices, entry.id) : null;

      return (
        <div key={`experience-info-player-${entry?.id}`} className="mb-1 flex align-items-center gap-2">
          {getPlayerStatusIndicatorWithNameAndLink(entry)}
          {roleKey && <span className="text-sm text-gray-500">{getRoleTitle(roles, roleKey)}</span>}
        </div>
      );
    });
  }, [experience, aggregatedExperiencePlayers, roles]);

  return (
    <InfoSection title="Players" icon="pi pi-desktop">
      {infoBody}
    </InfoSection>
  );
};
